// client/src/routes/app/tenants/new.tsx — SCR-TEN-002 (create) · TEN-004 (04 §8.4).
//
// `Leasing.Write`, not `Leasing.Read`: an accountant can see the tenants and cannot add one
// (03 §17.1), and the client mirror has to say so before the API's 403 does — the nav hides
// nothing here, because this URL is reached from a button the same mirror already hid.
//
// Creating lands on the new tenant's detail rather than back on the list. The landlord's next
// act is almost always to add the contacts, and that is a tab of the screen they are now on.
//
// „Adăugați chiriaș și contract" is the other next act (D-2880): the tenant is saved the same
// way, and the landlord goes straight on to the contract wizard with them set as its titular.
// The tenant is created first and the contract after, as two saves, rather than both in one —
// the wizard is five steps long and a tenant abandoned half-way through it is still a tenant.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { TenantForm } from '@/features/tenants/TenantForm';
import { useCreateTenant } from '@/features/tenants/queries';
import { toTenantProfileInput, type TenantFormValues } from '@/features/tenants/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/tenants/new')({
  component: NewTenantScreen,
});

function NewTenantScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateTenant();

  return (
    <RequirePermission permission="Leasing.Write">
      <PageHeader
        title={t('tenants.form.createTitle')}
        description={t('tenants.form.createLead')}
        backTo="/app/tenants"
        backLabel={t('tenants.list.title')}
      />
      <TenantForm
        submitLabel={t('tenants.form.create')}
        onSubmit={async (values: TenantFormValues) => {
          const created = await create.mutateAsync({ body: toTenantProfileInput(values) });
          toast.success(t('tenants.form.created'));
          await navigate({ to: '/app/tenants/$tenantId', params: { tenantId: created.id } });
        }}
        // The contract wizard asks for the same `Leasing.Write` this screen already required.
        alternateSubmit={{
          label: t('tenants.form.createWithContract'),
          onSubmit: async (values: TenantFormValues) => {
            const created = await create.mutateAsync({ body: toTenantProfileInput(values) });
            toast.success(t('tenants.form.createdOpeningContract'));
            await navigate({ to: '/app/contracts/new', search: { tenantId: created.id } });
          },
        }}
        onCancel={() => {
          void navigate({ to: '/app/tenants' });
        }}
      />
    </RequirePermission>
  );
}
